import { acceptHMRUpdate, defineStore } from "pinia";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { computed, ComputedRef, ref, Ref, watch } from "vue";
import {
    EventsAndOutrights,
    PrematchDrilldownNode,
    PrematchEventDrilldown,
    usePrematchStore,
} from "@/prematch/stores/prematch-sports";
import { Event, EventDrilldown } from "@/shared/model/event";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import useUtils from "@/shared/composables/use-utils";
import useMarketGrouping from "@/live/composables/use-market-grouping";
import useMock from "@/live/composables/use-mock";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { DRILLDOWN_SPORT_LEVEL_NUMBER } from "@/shared/utils/constants";
import DrilldownNodeUtils from "@/shared/utils/drilldown-node-utils";
import { computedAsync } from "@vueuse/core";
import { groupBy as _groupBy, omit as _omit, sortBy as _sortBy, uniqBy as _uniqBy } from "lodash";
import { RouteLocationNormalizedLoadedGeneric, useRoute, useRouter } from "vue-router";
import { PrematchStaticCategories } from "@/prematch/model/enums/prematch-static-categories";
import { waitUntilTrue } from "@/shared/utils/wait";
import { Market, MarketGroup } from "@/shared/model/market";

const drilldownNodesToPrematchDrilldownNodes = (drilldownNodes: DrilldownNode[][]) => {
    const newSports: PrematchDrilldownNode[] = [];
    const newCountries: PrematchDrilldownNode[] = [];
    const newLeagues: PrematchDrilldownNode[] = [];
    drilldownNodes?.forEach((node) => {
        const sport = node[2];
        const country = node[1];
        const league = node[0];
        newSports.push({ ...sport, id: sport.id, route: sport.id });
        newCountries.push({
            ...country,
            id: country.id,
            route: `${sport.id}-${country.id}`,
            sportId: sport.id,
        });
        newLeagues.push({
            ...league,
            id: league.id,
            route: `${sport.id}${DrilldownNodeUtils.isSportMultilevel(league.code) ? `-${country.id}` : ""}/${league.id}`,
            sportId: sport.id,
            countryId: country.id,
        });
    });
    return { sports: newSports, countries: newCountries, leagues: newLeagues };
};

const DEFAULT_PREMATCH_FETCH_INTERVAL = 120_000;
const DEFAULT_LIVE_FETCH_INTERVAL = 10_000;

export const usePrematchFavouritesStore = defineStore("PrematchFavourites", () => {
    const router = useRouter();
    const route = useRoute();
    const favouritesStore = useFavouritesStore();
    const drilldownNodesStore = useDrilldownNodesStore();
    // Shared logic with prematch store
    const {
        updateLeagues,
        setEvents,
        setOutrights,
        setHeadersSports,
        setHeadersCountries,
        getEventsAndOutrightsBySportCodeAndLeagueIds,
    } = usePrematchStore();
    const { sortByDisplayOrder, sortByNegativeDisplayOrder } = useUtils();
    const { getOddsByHeaders } = useMarketGrouping();
    const { getMarketPreviewConfig } = useMock();

    // This block of refs is used as a cache for further processing (do not export)
    const events: Ref<PrematchEventDrilldown[]> = ref([]);
    const outrights: Ref<Event[]> = ref([]);
    const sports: Ref<PrematchDrilldownNode[]> = ref([]);
    const countries: Ref<PrematchDrilldownNode[]> = ref([]);
    const leagues: Ref<PrematchDrilldownNode[]> = ref([]);

    const fetchInterval: Ref<number> = ref(0);
    const initialized: Ref<boolean> = ref(false);
    const wsDataUpdate = ref(false);
    const activeTab: Ref<string> = ref(); // persisted;
    const favoriteEventsLoaded: Ref<boolean> = ref(false);
    const favoriteLeaguesLoaded: Ref<boolean> = ref(false);

    const { getEventsDrilldown, getEventsDrilldownDetail } = useOpenbetApi();

    const isSomeEventLive = computed(() => events.value.some((e) => e.liveNow));

    const favouriteEvents: ComputedRef<PrematchEventDrilldown[]> = computed(() => {
        return events.value.filter(
            (event) => event.active && !event.resulted && favouritesStore.isFavouriteEvent(event.id),
        );
    });

    const favouriteOutrights: ComputedRef<Event[]> = computed(() =>
        outrights.value.filter(
            (outright) => outright.active && !outright.resulted && favouritesStore.isFavouriteEvent(outright.id),
        ),
    );

    const favouriteLeagues: ComputedRef<PrematchDrilldownNode[]> = computed(() => {
        return sortByNegativeDisplayOrder(
            leagues.value.filter((league) => favouritesStore.isFavouriteLeague(league.id)),
        );
    });

    const favouriteEventsCount: ComputedRef<number> = computed(
        () => favouriteEvents.value.length + favouriteOutrights.value.length,
    );
    const favouriteLeaguesCount: ComputedRef<number> = computed(() => favouriteLeagues.value.length);

    const sportsFromFavouriteEvents = computedAsync(async () => {
        const sportIdsFromFavouriteEvents = favouriteEvents.value.map((event) => event.drilldownNodes[2].id);
        const sportIdsFromFavouriteOutrights = await favouriteOutrights.value.mapAsync(
            async (outright) =>
                (await drilldownNodesStore.getDrilldownNodesById(outright.competitionDrilldownTagId))?.find(
                    (node) => node.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER,
                )?.id,
        );
        const sportIdsFromFavouriteEventsAndOutrights = [
            ...sportIdsFromFavouriteEvents,
            ...sportIdsFromFavouriteOutrights,
        ];
        return favouritesStore.sortByFavourites(
            sports.value.filter((sport) => sportIdsFromFavouriteEventsAndOutrights.includes(sport.id)),
            "sport",
        );
    }, []);

    const sportsFromFavouriteLeagues = computed(() => {
        const sportIdsFromFavouriteLeagues = favouriteLeagues.value.map((league) => league.sportId);
        return favouritesStore.sortByFavourites(
            sports.value.filter((sport) => sportIdsFromFavouriteLeagues.includes(sport.id)),
            "sport",
        );
    });

    const favouriteSportsDataSource = computed(() => {
        switch (activeTab.value) {
            case "events":
                return sportsFromFavouriteEvents.value;
            case "leagues":
                return sportsFromFavouriteLeagues.value;
            default:
                return [];
        }
    });

    const setFetchInterval = (interval: number) => {
        fetchInterval.value = interval;
    };

    const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
        await waitUntilTrue(initialized, time, timeout).catch(() => {
            logException("prematch-favourites", undefined, {
                message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
            });
        });
    };

    async function loadFavouriteEvents(): Promise<void> {
        try {
            if (!favouritesStore.favouriteEvents.length) return;

            favoriteEventsLoaded.value = false;

            const eventIds = favouritesStore.favouriteEvents?.map((eventId) => Number(eventId));

            // Stupidity par excellence...
            // We need here to group event ids by sport to get correct groupedMarkets, but first we need to load events by their ids from api to get drilldown nodes!
            const groupedEventsBySportCode = _groupBy(
                (await getEventsDrilldown({ eventIds })) || [],
                "drilldownNodes[2].code",
            );
            const eventDrilldownPromises: Promise<EventDrilldown[]>[] = [];

            Object.keys(groupedEventsBySportCode).forEach((key) => {
                const marketGroupTypesIncluded = Array.from(
                    new Set(getMarketPreviewConfig(key).map((m) => m.market_group_code)),
                );
                const marketsSortsIncluded = Array.from(new Set(getMarketPreviewConfig(key).map((m) => m.sort_code)));

                eventDrilldownPromises.push(
                    getEventsDrilldown(
                        {
                            eventIds: groupedEventsBySportCode[key].map((e) => e.id),
                            marketGroupTypesIncluded:
                                marketGroupTypesIncluded.length > 0 ? marketGroupTypesIncluded : undefined,
                            marketsSortsIncluded: marketsSortsIncluded.length > 0 ? marketsSortsIncluded : undefined,
                            groupedMarkets: true,
                        },
                        { wsData: { update: wsDataUpdate.value } },
                    ),
                );
            });
            const results = await Promise.allSettled(eventDrilldownPromises);
            let _response: EventDrilldown[] = [];
            results.forEach((result) => {
                if (result.status === "fulfilled") {
                    _response = _response.concat(result.value);
                }
            });
            //TODO: filtering out 12 market outcome, should be done on MW side later, when it's done remove this. (needed to improve the logic since 12 market started to come as !name instead of null or 12)
            const response: EventDrilldown[] = _response?.map((evt) => {
                const updatedEvent = {
                    ...evt,
                    groupedMarkets: evt.groupedMarkets?.map((gm) => ({
                        ...gm,
                        outcomes:
                            gm.groupCode === "CUSTOM"
                                ? gm.outcomes.filter((o) => o.name !== null && o.name !== "12" && o.name)
                                : gm.outcomes.filter((o) => o.name),
                    })) as (Market | MarketGroup)[],
                };

                // Add getOddsByHeaders for each event
                updatedEvent.oddsByHeaders = getOddsByHeaders(evt.drilldownNodes[0], updatedEvent, true);

                return updatedEvent;
            });
            const returnedEventIds = new Set(response?.map((event) => event.id) || []);
            const missingEventIds = eventIds.filter((id) => !returnedEventIds.has(id.toString()));
            //TODO: this approach is not optimal. The issue is that we can't really say what event is outright and what event is classic event. But they are treated differently in our app. We should have a better way to distinguish them.
            //Outrights are not returned in getEventsDrilldown. And that's a good thing, or we could not distinguish them from classic events.

            response?.forEach((event) => {
                const eventExists = events.value.some((existingEvent) => existingEvent.id === event.id);
                const eventLeague = leagues.value.find((l) => l.id === event.drilldownNodes[0]?.id);
                if (eventLeague) {
                    eventLeague.eventsAndOutrightsLoaded = true;
                    eventLeague.showInFavouriteEvents = true;
                }

                if (!eventExists) {
                    events.value.push(event);
                    const {
                        sports: newSports,
                        countries: newCountries,
                        leagues: newLeagues,
                    } = drilldownNodesToPrematchDrilldownNodes([event.drilldownNodes]);

                    sports.value = _uniqBy([...sports.value, ...newSports], "id");
                    countries.value = _uniqBy([...countries.value, ...newCountries], "id");
                    leagues.value = _uniqBy([...leagues.value, ...newLeagues], "id");
                }
            });
            if (missingEventIds.length > 0) {
                const outrightsResponse = await getEventsDrilldownDetail({
                    eventIds: missingEventIds,
                });

                outrightsResponse?.forEach((outright) => {
                    const outrightExists = outrights.value.some((existingEvent) => existingEvent.id === outright.id);
                    const outrightLeague = leagues.value.find((l) => l.id === outright.competitionDrilldownTagId);

                    if (outrightLeague) {
                        outrightLeague.eventsAndOutrightsLoaded = true;
                        outrightLeague.showInFavouriteEvents = true;
                    }

                    if (!outrightExists) {
                        // Sort the markets by displayOrder
                        const sortedMarkets = _sortBy(outright.markets, "displayOrder").map((market) => ({
                            ...market,
                            // Sort the market outcomes by displayOrder and then by price
                            outcomes: _sortBy(market.outcomes, ["displayOrder", (o) => o.prices[0].decimal]),
                        }));

                        outrights.value.push({ ...outright, markets: sortedMarkets });
                        const {
                            sports: newSports,
                            countries: newCountries,
                            leagues: newLeagues,
                        } = drilldownNodesToPrematchDrilldownNodes([outright.drilldownNodes]);

                        sports.value = _uniqBy([...sports.value, ...newSports], "id");
                        countries.value = _uniqBy([...countries.value, ...newCountries], "id");
                        leagues.value = _uniqBy([...leagues.value, ...newLeagues], "id");
                    }
                });
            }
        } catch (error) {
            logException("prematch-favourites", error, {
                message: "An error occurred while fetching favourite events",
            });
            return Promise.reject(new Error(error));
        } finally {
            favoriteEventsLoaded.value = true;
        }

        return Promise.resolve();
    }

    const getEventsAndOutrights = async (sportCode: string, leagueIds: number[]): Promise<EventsAndOutrights> =>
        await getEventsAndOutrightsBySportCodeAndLeagueIds(sportCode, leagueIds, true, false);

    const loadFavouriteLeagues = async () => {
        if (!favouritesStore.favouriteLeagues.length) return;
        favoriteLeaguesLoaded.value = false;
        const favouriteDrilldownNodesPromises: Promise<DrilldownNode[]>[] = [];
        favouritesStore.favouriteLeagues.forEach((leagueId) => {
            favouriteDrilldownNodesPromises.push(drilldownNodesStore.getDrilldownNodesById(leagueId));
        });
        const results = await Promise.allSettled(favouriteDrilldownNodesPromises);
        const favouriteDrilldownNodes: DrilldownNode[][] = [];
        results.forEach((result) => {
            if (result.status === "fulfilled") {
                favouriteDrilldownNodes.push(result.value);
            }
        });

        const {
            sports: newSports,
            countries: newCountries,
            leagues: newLeagues,
        } = drilldownNodesToPrematchDrilldownNodes(favouriteDrilldownNodes);

        sports.value = _uniqBy([...sports.value, ...newSports], "id");
        countries.value = _uniqBy([...countries.value, ...newCountries], "id");
        leagues.value = _uniqBy([...leagues.value, ...newLeagues], "id");

        const leaguesToLoadGrouped = _groupBy(newLeagues, "code");
        const getEventsAndOutrightsPromises: Promise<EventsAndOutrights>[] = [];
        let leagueIds = [];

        Object.keys(leaguesToLoadGrouped).forEach((key) => {
            const groupedLeagueIds = leaguesToLoadGrouped[key].map((league) => Number(league.id));
            leagueIds = [...leagueIds, ...groupedLeagueIds];
            getEventsAndOutrightsPromises.push(getEventsAndOutrights(key, groupedLeagueIds));
        });
        const res = await Promise.allSettled(getEventsAndOutrightsPromises);
        const eventsAndOutrights: EventsAndOutrights[] = [];
        res.forEach((result) => {
            if (result.status === "fulfilled") {
                eventsAndOutrights.push(result.value);
            }
        });

        eventsAndOutrights.forEach((eo) => {
            setEvents(eo.events, events.value);
            setOutrights(eo.outrights, outrights.value);
            updateLeagues(leagueIds, leagues.value, (leagueId) => getEventsByLeagueId(leagueId, false));
            setHeadersSports(new Set(newSports.map((s) => s.id)), sports.value, events.value);
            setHeadersCountries(new Set(newCountries.map((c) => c.id)), countries.value, events.value);
        });
        favoriteLeaguesLoaded.value = true;
    };

    const getLeaguesWithFavourites = (): PrematchDrilldownNode[] => {
        const uniqueLeaguesMap = new Map<string, PrematchDrilldownNode>();

        favouriteEvents.value.forEach((event) => {
            const leagueId = event.drilldownNodes[0].id;
            const league = leagues.value.find((l) => l.id === leagueId);
            if (league) {
                event.oddsByHeaders = getOddsByHeaders(league, event, true);

                if (!uniqueLeaguesMap.has(leagueId)) {
                    uniqueLeaguesMap.set(leagueId, league);
                }
            }
        });

        favouriteOutrights.value.forEach((outright) => {
            const leagueId = outright.competitionDrilldownTagId;
            const league = leagues.value.find((l) => l.id === leagueId);
            if (league) {
                if (!uniqueLeaguesMap.has(leagueId)) {
                    uniqueLeaguesMap.set(leagueId, league);
                }
            }
        });

        return Array.from(uniqueLeaguesMap.values());
    };

    const getEventsByLeagueId = (leagueId: string, favouritesOnly: boolean = true) =>
        sortByDisplayOrder(favouritesOnly ? favouriteEvents.value : events.value).filter(
            (event) => event.competitionDrilldownTagId === leagueId,
        );

    const getOutrightsByLeagueId = (leagueId: string, favouritesOnly: boolean = true): Event[] =>
        (favouritesOnly ? favouriteOutrights.value : outrights.value).filter(
            (outright) => outright.competitionDrilldownTagId === leagueId,
        );

    const loadFavouriteEventsAndLeagues = async () => {
        await loadFavouriteEvents();
        await loadFavouriteLeagues();
    };

    // Set default active tab based on received data
    const setActiveTab = () => {
        if (favouriteEventsCount.value) {
            activeTab.value = "events";
        } else if (favouriteLeaguesCount.value) {
            activeTab.value = "leagues";
        } else {
            activeTab.value = "";
        }
    };

    let intervalId = null;
    watch(
        fetchInterval,
        async (newValue) => {
            if (newValue) {
                if (!initialized.value) {
                    await favouritesStore.waitUntilInitialized();
                    await loadFavouriteEventsAndLeagues();
                    setActiveTab();
                    initialized.value = true;
                }
                if (intervalId) clearInterval(intervalId);
                intervalId = setInterval(async () => {
                    await loadFavouriteEventsAndLeagues();
                }, fetchInterval.value);
            } else if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
                initialized.value = false;
            }
        },
        { immediate: true },
    );

    const calculateFetchInterval = (isSomeEventLive: boolean, curRoute: RouteLocationNormalizedLoadedGeneric) => {
        if (curRoute.name === "PrematchSport" && curRoute.path.includes(`/${PrematchStaticCategories.FAVORITES}`)) {
            wsDataUpdate.value = true;
            return isSomeEventLive ? DEFAULT_LIVE_FETCH_INTERVAL : DEFAULT_PREMATCH_FETCH_INTERVAL;
        }
        //TODO: This can be fixed with almost no performance impact to load favourite events on the first load + set fetch interval to f.e. 1 hour to display the number of favourite in navigation - LATER
        // else if (curRoute.fullPath.startsWith(PATHS.PREMATCH) || curRoute.name === "Homepage") {
        //     // Sad that we need to load this big chunk of data everywhere on prematch just because business wants to display some number in menu :(
        //     wsDataUpdate.value = false;
        //     return DEFAULT_PREMATCH_FETCH_INTERVAL;
        // }
        wsDataUpdate.value = false;
        return 0;
    };

    watch(wsDataUpdate, (newValue, oldValue) => {
        // if wsDataUpdate changes to true we want to apply ws updates ASAP, otherwise if false we'll wait for next fetch cycle (where ws updates will be dismissed anyway) or removed from API store completely by changing the route
        if (newValue && newValue !== oldValue && initialized.value) {
            loadFavouriteEventsAndLeagues();
        }
    });

    watch(
        route,
        (newValue) => {
            setFetchInterval(calculateFetchInterval(isSomeEventLive.value, newValue));

            if (!activeTab.value) setActiveTab();
        },
        { immediate: true },
    );

    watch(
        () => isSomeEventLive.value && initialized.value,
        (newValue) => {
            if (!fetchInterval.value) return;
            setFetchInterval(calculateFetchInterval(newValue, route));
        },
    );

    watch(
        [() => favouritesStore.favouriteEvents.length, () => favouritesStore.favouriteLeagues.length],
        ([favouriteEventsCountNew, favouriteLeaguesCountNew], [favouriteEventsCountOld, favouriteLeaguesCountOld]) => {
            if (favouriteEventsCountNew > favouriteEventsCountOld) loadFavouriteEvents();
            if (favouriteLeaguesCountNew > favouriteLeaguesCountOld) loadFavouriteLeagues();
            if (
                (favouriteEventsCountNew < favouriteEventsCountOld && !favouriteEventsCountNew) ||
                (favouriteLeaguesCountNew < favouriteLeaguesCountOld && !favouriteLeaguesCountNew)
            ) {
                setActiveTab();
            }
        },
    );

    watch(activeTab, (newValue, oldValue) => {
        if (
            newValue !== oldValue &&
            ["events", "leagues"].includes(newValue) &&
            ["events", "leagues"].includes(oldValue) &&
            route.query?.eventId
        ) {
            router.replace({
                query: _omit(route.query, "eventId"),
            });
        }
    });

    return {
        initialized,
        wsDataUpdate,
        activeTab,
        events,
        outrights,
        sports,
        countries,
        leagues,
        favoriteEventsLoaded,
        favoriteLeaguesLoaded,
        favouriteEvents,
        favouriteOutrights,
        favouriteLeagues,
        favouriteEventsCount,
        favouriteLeaguesCount,
        sportsFromFavouriteEvents,
        sportsFromFavouriteLeagues,
        favouriteSportsDataSource,
        waitUntilInitialized,
        setFetchInterval,
        getLeaguesWithFavourites,
        getEventsByLeagueId,
        getOutrightsByLeagueId,
    };
});

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePrematchFavouritesStore, import.meta.hot));
}
